import { v4 as uuid } from 'uuid';

import { Config } from '@config/base';
import { SystemId } from '@config/system-id';
import { TenantIds } from '@config/tenant-id';

import { SBaseDisplay, SDate } from '@summit/core';
import { DayOfWeek, SummitFormattedText } from '@summit/models';

import { ReportConfig } from '@app/models/reports2/report-config';
import { EboObject } from '../base-classes/ebo-object';
import { Employee } from '../employee/employee';
import { EmployeePayrollGroup } from '../employee/employee-payroll-group';
import { Company } from '../organization/company/company';
import { State } from '../places/state/state';
import { RoundingMethod } from '../shared/rounding-method.enum';
import { TruckColumns } from '../shared/truck-columns.enum';
import { TenantScheduleParameter } from '../tenant-schedule-parameter';
import { TruckType, TruckTypeMapping } from '../truck/truck-type.enum';
import { FinanceParameter } from './finance-parameter';
import { FinancialSalaryDistribution } from './financial-salary-distribution.enum';
import { LogEntryMethod } from './log-entry-method.enum';
import { PayrollBreakdown } from './payroll-breakdown.enum';
import { PayrollFormat } from './payroll-format.enum';
import { PendingImplementation } from './pending-implementation.enum';
import { ServiceMapping, ServiceProvider } from './service-provider.enum';
import { TenantCredential } from './tenant-credential';
import { TenantFeatures } from './tenant-features';
import { TenantPayrollParameter } from './tenant-payroll-parameter';
import { TenantReconcileProcessParameter } from './tenant-reconcile-process-parameter';
import { TenantStatusFlags, TenantStatusFlagsMapping } from './tenant-status-flags.enum';
import { TenantSubscription } from './tenant-subscription';

export class Tenant extends EboObject implements SBaseDisplay {
  name: string;
  abbreviation: string;
  endOfWeek: DayOfWeek;
  endOfScheduleWeek: DayOfWeek;
  endOfIvmrWeek: DayOfWeek;
  stateID: string;
  state: State;
  defaultTruckType: TruckType;
  status: TenantStatusFlags;
  finance: FinanceParameter;
  payroll: TenantPayrollParameter;
  payrollGroups: EmployeePayrollGroup[];
  reconcileProcess: TenantReconcileProcessParameter;
  systemEmployeeID: string;
  systemEmployee: Employee;
  defaultCompanyID: string;
  defaultCompany: Company;
  credentials: TenantCredential[];
  subscriptions: TenantSubscription[];
  testingOffset: number;
  scheduleConfig: TenantScheduleParameter;

  currentSubscription: TenantSubscription;
  //Form Fields
  featureChange: TenantFeatures; //used to change next subscription

  //financial
  showSettlementGross: boolean;

  //Other Info
  ivmrAutoCreate: boolean;
  ivmrRoundingMethod: RoundingMethod;
  ivmrEmailSkipTerminal: boolean;
  //syncIvmrOffsetToMaintenance: boolean;
  odometerDaysOfHistory: number;
  maintOdometerSyncWithIvmr: boolean;
  inboxAddress: string;
  regulatoryThresholdFlag: number;
  regulatoryThresholdWarn: number;
  regulatoryThresholdAlarm: number;

  //Hack for typing
  reportsTruckSort: TruckColumns;
  salaryDistribution: FinancialSalaryDistribution;
  entryMethod: LogEntryMethod;
  breakdown: PayrollBreakdown;
  format: PayrollFormat;
  pendingImplementation: PendingImplementation;
  //Reconcile Fields
  reconcileOrder: number;
  reconciler: string;
  reconcileNotes: string;

  constructor(jsonData: Partial<Tenant>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.state) {
        this.state = new State(jsonData.state);
      }
      if (jsonData.systemEmployee) {
        this.systemEmployee = new Employee(jsonData.systemEmployee);
      }
      if (jsonData.defaultCompany) {
        this.defaultCompany = new Company(jsonData.defaultCompany);
      }
      if (jsonData.payrollGroups)
        this.payrollGroups = jsonData.payrollGroups.map((entry) => new EmployeePayrollGroup(entry));
      if (jsonData.subscriptions)
        this.subscriptions = jsonData.subscriptions.map((entry) => new TenantSubscription(entry));

      this.finance = new FinanceParameter(jsonData.finance);
      this.payroll = new TenantPayrollParameter(jsonData.payroll);
      this.reconcileProcess = new TenantReconcileProcessParameter(jsonData.reconcileProcess);
      this.scheduleConfig = new TenantScheduleParameter(jsonData.scheduleConfig);
      if (jsonData.credentials) {
        this.credentials = jsonData.credentials
          .map((entry) => new TenantCredential(entry))
          .sort((a, b) => {
            if (ServiceMapping[a.service] < ServiceMapping[b.service]) {
              return -1;
            }
            if (ServiceMapping[a.service] > ServiceMapping[b.service]) {
              return 1;
            }
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
      }
    }
    this.id ??= uuid();
    this.credentials ??= [];
    this.finance ??= new FinanceParameter();
    this.payroll ??= new TenantPayrollParameter();
    this.payrollGroups ??= [];
    this.reconcileProcess ??= new TenantReconcileProcessParameter();
    this.scheduleConfig ??= new TenantScheduleParameter();
    this.subscriptions ??= [];
    this.currentSubscription = this.subscriptions
      .filter((sub) => sub.effectiveDate.isSameOrBefore(SDate.newUtc()))
      .sort((a, b) => (a.effectiveDate.isBefore(b.effectiveDate) ? 1 : -1))[0];
    this.endOfWeek ??= Config.defaultEndOfWeek;
    this.endOfScheduleWeek ??= Config.defaultEndOfScheduleWeek;
    this.endOfIvmrWeek ??= Config.defaultEndOfIvmrWeek;
    this.defaultTruckType ??= TruckType.Unknown;
    this.testingOffset ??= 0;
    this.ivmrRoundingMethod ??= RoundingMethod.Round;
    // this.maintOdometerSyncWithIvmr ??= false; // testing the default value for maintOdometerSyncWithIvmr

    if (this.id.toUpperCase() == TenantIds.SandT) {
      this.showSettlementGross = true;
    }
  }

  HasFlag(flag: TenantStatusFlags): boolean {
    return flag === (this.status & flag);
  }

  SetFlag(flag: TenantStatusFlags, value: boolean) {
    if (value) {
      this.status |= flag;
    } else {
      this.status &= ~flag;
    }
  }

  isHidden(): boolean {
    return this.HasFlag(TenantStatusFlags.Hidden);
  }

  toShortString(): string {
    return `${this.name} (${this.abbreviation})`;
  }

  isSystem(): boolean {
    return this.id.toUpperCase() == SystemId.SystemTenantID;
  }

  get currentInvoiceCycleDate(): number {
    return this.currentSubscription.invoiceCycleDate;
  }
  get currentPricePerTruck(): number {
    return this.currentSubscription.pricePerTruck;
  }
  get currentDiscount(): number {
    return this.currentSubscription.discount;
  }
  get currentTruckOverride(): number {
    return this.currentSubscription.truckOverride;
  }
  get currentFeatures(): TenantFeatures {
    return this.currentSubscription.features;
  }
  get currentSubComment(): number {
    return this.currentSubscription.comment;
  }

  get currentCredentials(): TenantCredential[] {
    return this.credentials.filter((entry) => !entry.archived);
  }

  get featureSummary(): string {
    if (this.currentSubscription.features.payroll) {
      return 'Automate';
    }
    return 'Organize';
  }

  get credentialTypes(): string {
    return this.credentials
      .map((entry) => entry.service)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((entry) => ServiceMapping[entry])
      .sort()
      .join(', ');
  }

  get hasFedExAccount(): boolean {
    return this.credentials.some((entry) => entry.service === ServiceProvider.FedEx && entry.archived === false);
  }

  get defaultTruckTypeFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.defaultTruckType
      ? response.AddLine(TruckTypeMapping[this.defaultTruckType])
      : response.AddLine(TruckTypeMapping[TruckType.Unknown]);
    return response;
  }
  get statusFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.HasFlag(TenantStatusFlags.Hidden)) {
      response.AddLine(`${TenantStatusFlagsMapping[TenantStatusFlags.Hidden]}\n`);
    }
    if (this.HasFlag(TenantStatusFlags.EarlyPayrollWeek)) {
      response.AddLine(`${TenantStatusFlagsMapping[TenantStatusFlags.EarlyPayrollWeek]}\n`);
    }
    if (this.HasFlag(TenantStatusFlags.OverrideReconcileLock)) {
      response.AddLine(`${TenantStatusFlagsMapping[TenantStatusFlags.OverrideReconcileLock]}\n`);
    }
    return response;
  }

  getTimeZone(): string | undefined {
    switch (this.id.toUpperCase()) {
      case TenantIds.ALC:
      case TenantIds.GGC:
        //Converted over to utc in DB
        return 'America/New_York';
      default:
        return undefined;
        break;
    }
  }

  getDriverLogEmailConfig(): ReportConfig {
    switch (this.id.toUpperCase()) {
      case TenantIds.BEI:
        return new ReportConfig({
          includeExtraItems: true,
          onlyRejected: true
        });
      case TenantIds.JWI:
        return new ReportConfig({
          includeLegEstimate: true,
          includeSpotEstimate: true
        });
      case TenantIds.Sophia:
        return new ReportConfig({
          includeLegEstimate: true
        });
      default:
        return new ReportConfig();
    }
  }
}
